import React, { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Stepper, Step, StepLabel, StepContent, Typography, Button } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeStepper — MUI Stepper for multi-step config wizards.
 * Used by API Req/Res (4 steps), Webhook (8 steps).
 */

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  '& .MuiStepper-root': {
    padding: 0,
  },
  '& .MuiStepLabel-label': {
    fontSize: NODE_UI.fontSize.value,
    fontWeight: NODE_UI.fontWeight.label,
  },
  '& .MuiStepLabel-label.Mui-active': {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: theme.palette.text.secondary,
  },
  '& .MuiStepIcon-root': {
    fontSize: 20,
    color: theme.palette.divider,
    '&.Mui-active': { color: theme.palette.text.primary },
    '&.Mui-completed': { color: theme.palette.text.primary },
  },
  '& .MuiStepContent-root': {
    borderLeft: `1px solid ${theme.palette.divider}`,
    paddingLeft: 16,
    marginLeft: 10,
  },
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.divider,
  },
}));

const StepActions = styled('div')({
  display: 'flex',
  gap: 8,
  marginTop: 12,
});

const StepButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: NODE_UI.fontSize.value,
  fontWeight: NODE_UI.fontWeight.label,
  padding: '4px 12px',
  minHeight: 28,
  borderRadius: NODE_UI.borderRadiusSm,
}));

const NodeStepper = React.memo(function NodeStepper({
  steps = [],
  activeStep: controlledStep,
  onStepChange,
  orientation = 'vertical',
  showNavButtons = true,
  sx,
}) {
  const [internalStep, setInternalStep] = useState(0);
  const activeStep = controlledStep ?? internalStep;

  const handleNext = useCallback(() => {
    const next = Math.min(activeStep + 1, steps.length - 1);
    onStepChange ? onStepChange(next) : setInternalStep(next);
  }, [activeStep, steps.length, onStepChange]);

  const handleBack = useCallback(() => {
    const prev = Math.max(activeStep - 1, 0);
    onStepChange ? onStepChange(prev) : setInternalStep(prev);
  }, [activeStep, onStepChange]);

  const handleStepClick = useCallback((index) => {
    onStepChange ? onStepChange(index) : setInternalStep(index);
  }, [onStepChange]);

  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      <Stepper activeStep={activeStep} orientation={orientation} nonLinear>
        {steps.map((step, index) => (
          <Step key={step.label || index} completed={step.completed ?? index < activeStep}>
            <StepLabel
              onClick={() => handleStepClick(index)}
              sx={{ cursor: 'pointer' }}
              optional={step.optional ? (
                <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: 'text.secondary' }}>
                  {step.optional}
                </Typography>
              ) : undefined}
            >
              {step.label}
            </StepLabel>
            {orientation === 'vertical' && (
              <StepContent>
                {step.content}
                {showNavButtons && (
                  <StepActions>
                    <StepButton
                      variant="contained"
                      size="small"
                      onClick={handleNext}
                      disabled={index === steps.length - 1}
                      disableElevation
                    >
                      {index === steps.length - 1 ? 'Finish' : 'Continue'}
                    </StepButton>
                    {index > 0 && (
                      <StepButton size="small" onClick={handleBack}>
                        Back
                      </StepButton>
                    )}
                  </StepActions>
                )}
              </StepContent>
            )}
          </Step>
        ))}
      </Stepper>
    </Root>
  );
});

export default NodeStepper;
